import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { observer } from 'mobx-react-lite';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CardContent, CardTitle } from '@/components/ui/card';
import { X, Plus, Mail } from 'lucide-react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useTeammate } from '@/hooks/useTeammate';
import { TEAMATEROLE } from '@/constants';
import { teamMateStore } from '@/state/TeammateStore';
import Spinner from '@/components/atoms/Spinner';

interface Invite {
  id: string;
  email: string;
  role: string;
}

const roles = [
  {
    value: TEAMATEROLE.OWNER,
    label: TEAMATEROLE.OWNER,
    description:
      'Full control of SparkHub including settings, users, billing, and account ownership.',
  },
  {
    value: TEAMATEROLE.ADMIN,
    label: TEAMATEROLE.ADMIN,
    description:
      'Manage most settings, users, billing, and integrations, except deleting the account.',
  },
  {
    value: TEAMATEROLE.MANAGER,
    label: TEAMATEROLE.MANAGER,
    description:
      'Handle campaigns, customer data, reports, and analytics but not users or billing.',
  },
  {
    value: TEAMATEROLE.AGENT,
    label: TEAMATEROLE.AGENT,
    description:
      'Respond to customers, view analytics, and trigger campaigns with limited access.',
  },
];

const InvitePeople = () => {
  const [invites, setInvites] = useState<Invite[]>([
    { id: '1', email: '', role: '' },
  ]);
  const [defaultRole, _] = useState('Agent');
  const { sendTeamMateInvite } = useTeammate();

  const addInvite = () => {
    const newInvite: Invite = {
      id: Date.now().toString(),
      email: '',
      role: defaultRole,
    };
    setInvites([...invites, newInvite]);
  };
  const navigate = useNavigate();

  const removeInvite = (id: string) => {
    if (invites.length > 1) {
      setInvites(invites.filter(invite => invite.id !== id));
    }
  };

  const updateInvite = (id: string, field: keyof Invite, value: string) => {
    setInvites(
      invites.map(invite =>
        invite.id === id ? { ...invite, [field]: value } : invite,
      ),
    );
  };

  const handleSendInvitations = async () => {
    const validInvites = invites
      ?.filter(invite => invite.email && invite.role)
      ?.map(({ email, role }) => ({ email, role }));
    await sendTeamMateInvite(validInvites);
  };

  const validInvites = invites.filter(invite => invite.email && invite.role);

  const renderButtonContent = () => {
    if (teamMateStore.inviting) {
      return (
        <div className='flex items-center gap-2'>
          <Spinner size={20} color='#ffffff' />
          Sending...
        </div>
      );
    }

    return `Send ${
      validInvites.length > 0 ? validInvites.length + ' ' : ''
    }invitation${validInvites.length !== 1 ? 's' : ''}`;
  };

  return (
    <div className='min-h-screen p-4'>
      <div className='mx-auto w-full'>
        <div className='border-b py-8'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <div
                onClick={() => navigate(-1)}
                className='w-[34px] h-[34px] flex-none  cursor-pointer rounded-[4px] flex justify-center items-center bg-[#FBFCFD] border border-border'
              >
                <Icon
                  icon='ph:arrow-left-light'
                  width={18}
                  height={18}
                  color='#000000'
                />
              </div>
              <div>
                <CardTitle className='text-lg font-medium'>
                  Invite teammates
                </CardTitle>
              </div>
            </div>
            <div className='flex gap-2'>
              <Button onClick={() => navigate(-1)} variant='outline' size='sm'>
                Cancel
              </Button>
              <Button
                size='sm'
                variant='secondary'
                onClick={handleSendInvitations}
                disabled={validInvites.length === 0 || teamMateStore?.inviting}
                className='text-white'
              >
                {renderButtonContent()}
              </Button>
            </div>
          </div>
        </div>

        <CardContent className='p-6 space-y-6'>
          <div className='space-y-4'>
            <div>
              <Label className='text-md font-medium'>
                Invite new teammates
              </Label>
              <p className='text-md text-gray-500 mt-1'>
                Add email address and assign role for each team member
              </p>
            </div>

            <div className='space-y-3'>
              {invites.map(invite => (
                <div key={invite.id} className='flex gap-3 items-start'>
                  <div className='flex-1'>
                    <Input
                      type='email'
                      placeholder='Enter email address'
                      value={invite.email}
                      onChange={e =>
                        updateInvite(invite.id, 'email', e.target.value)
                      }
                      className='w-full focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-ring focus-visible:ring-offset-0 focus-within:border focus-within:border-secondary'
                    />
                  </div>
                  <div className='w-[500px]'>
                    <Select
                      value={invite.role}
                      onValueChange={value =>
                        updateInvite(invite.id, 'role', value)
                      }
                    >
                      <SelectTrigger className='focus:ring-0 focus:ring-offset-0 focus-within:border focus-within:border-secondary'>
                        <SelectValue placeholder='Select role' />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map(role => (
                          <SelectItem key={role.value} value={role.value}>
                            <div className='flex flex-col'>
                              <span className='font-medium'>{role.label}</span>
                              <span className='text-xs text-gray-500 truncate'>
                                {role.description}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {invites.length > 1 && (
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => removeInvite(invite.id)}
                      className='p-2 text-gray-400 hover:text-gray-600'
                    >
                      <X className='h-4 w-4' />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {validInvites.length ? (
              <Button
                variant='outline'
                onClick={addInvite}
                className='w-full border-dashed'
              >
                <Plus className='h-4 w-4 mr-2' />
                Add another teammate
              </Button>
            ) : null}

            {validInvites.length > 0 && (
              <div className='bg-secondary/20 border border-secondary/40 rounded-lg p-4'>
                <div className='flex items-center gap-2 text-secondary/80 text-md font-medium mb-2'>
                  <Mail className='h-4 w-4' />
                  Ready to send {validInvites.length} invitation
                  {validInvites.length !== 1 ? 's' : ''}
                </div>
                <div className='space-y-1'>
                  {validInvites.map(invite => (
                    <div key={invite.id} className='text-md text-secondary/70'>
                      {invite.email} •{' '}
                      {roles.find(r => r.value === invite.role)?.label}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </div>
    </div>
  );
};
export default observer(InvitePeople);
